/**
 * notify-permission-dialog.tsx — Hộp xin quyền bật thông báo (trước khi hiện hộp xin quyền của trình duyệt).
 *
 * Trình duyệt chỉ cho hỏi quyền khi người dùng bấm, và đã chặn rồi thì không hỏi lại được — nên hỏi bằng hộp của mình trước,
 * giải thích lợi ích; chỉ khi bấm "Bật thông báo" mới gọi Notification.requestPermission(). Đồng ý → bật luôn tuỳ chọn
 * "Thông báo trình duyệt" trong Cài đặt cá nhân (nếu không thì có quyền mà vẫn không thông báo gì). "Để sau" / đóng → hoãn vài ngày.
 */
import { useTranslation } from 'react-i18next'
import { Bell, BellRing, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { snooze } from '@/lib/pwa-install'
import { useEnableNotifications } from '@/hooks/use-enable-notifications'

export function NotifyPermissionDialog({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation('sharedMisc')
  const { enable: enableNotifications, busy } = useEnableNotifications()

  const later = () => {
    snooze('notify')
    onDone()
  }

  const enable = async () => {
    // Đóng hộp của trình duyệt mà không chọn → hoãn; đã bật / bị chặn thì không cần hoãn (không hỏi lại được nữa).
    if ((await enableNotifications()) === 'dismissed') snooze('notify')
    onDone()
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) later() }}>
      <DialogContent className="max-w-sm gap-4 overflow-hidden p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-6 pb-3 pt-7 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30">
            <BellRing className="h-7 w-7" />
          </span>
          <DialogHeader className="mt-4 space-y-1.5 text-center sm:text-center">
            <DialogTitle className="text-lg">{t('notifyPermissionDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('notifyPermissionDialog.description')}
            </DialogDescription>
          </DialogHeader>
        </div>
        <ul className="space-y-2 px-6 text-sm">
          <li className="flex items-center gap-2.5"><Bell className="h-4 w-4 shrink-0 text-primary" /> {t('notifyPermissionDialog.bullet1')}</li>
          <li className="flex items-center gap-2.5"><MessageSquare className="h-4 w-4 shrink-0 text-primary" /> {t('notifyPermissionDialog.bullet2')}</li>
        </ul>
        <DialogFooter className="flex-col gap-2 px-6 pb-6 sm:flex-col sm:space-x-0">
          <Button className="w-full" size="lg" onClick={() => void enable()} disabled={busy}>
            <BellRing /> {t('notifyPermissionDialog.enable')}
          </Button>
          <Button className="w-full" variant="ghost" onClick={later} disabled={busy}>{t('notifyPermissionDialog.later')}</Button>
          <p className="text-center text-[11px] text-muted-foreground">{t('notifyPermissionDialog.hint')}</p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
